import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import {
  SimBatchesTab,
  SimRunsTab,
  SimScenariosTab,
  SimSummaryTab,
  RunSimulationDialog,
} from './components'
import type { SimBatch } from '@/types/simulation'

type TabKey = 'batches' | 'runs' | 'scenarios' | 'summary'

/**
 * Main Simulations page with tabs for Batches, Runs, Scenarios, and Summary
 */
export function SimulationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  // Determine initial tab from URL
  const getInitialTab = (): TabKey => {
    const path = location.pathname
    if (path.includes('/runs')) return 'runs'
    if (path.includes('/scenarios')) return 'scenarios'
    if (path.includes('/summary')) return 'summary'
    return 'batches'
  }

  const [activeTab, setActiveTab] = useState<TabKey>(getInitialTab())
  const [isRunDialogOpen, setIsRunDialogOpen] = useState(false)
  const [rerunBatch, setRerunBatch] = useState<SimBatch | null>(null)

  const handleTabChange = (key: TabKey) => {
    setActiveTab(key)
    // Update URL to reflect the active tab
    navigate(`/simulations/${key}`, { replace: true })
  }

  const handleRunSuccess = () => {
    // After running a simulation, switch to batches tab to see progress
    handleTabChange('batches')
  }

  const handleRerunBatch = useCallback((batch: SimBatch) => {
    setRerunBatch(batch)
    setIsRunDialogOpen(true)
  }, [])

  const handleCloseDialog = useCallback(() => {
    setIsRunDialogOpen(false)
    setRerunBatch(null)
  }, [])

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t('pages.simulations.title')} />

      <div className="flex-1 overflow-auto p-4 md:p-6 lg:p-8">
        <Tabs
          selectedKey={activeTab}
          onSelectionChange={(key) => handleTabChange(key as TabKey)}
        >
          <div className="border-b border-gray-200 dark:border-gray-700">
            <TabsList>
              <TabsTrigger id="batches">{t('pages.simulations.tabs.batches')}</TabsTrigger>
              <TabsTrigger id="runs">{t('pages.simulations.tabs.runs')}</TabsTrigger>
              <TabsTrigger id="scenarios">{t('pages.simulations.tabs.scenarios')}</TabsTrigger>
              <TabsTrigger id="summary">{t('pages.simulations.tabs.summary')}</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent id="batches" className="flex-1 pt-4">
            <SimBatchesTab
              onRunSimulation={() => setIsRunDialogOpen(true)}
              onRerunBatch={handleRerunBatch}
            />
          </TabsContent>

          <TabsContent id="runs" className="flex-1 pt-4">
            <SimRunsTab />
          </TabsContent>

          <TabsContent id="scenarios" className="flex-1 pt-4">
            <SimScenariosTab />
          </TabsContent>

          <TabsContent id="summary" className="flex-1 p-4">
            <SimSummaryTab />
          </TabsContent>
        </Tabs>
      </div>

      <RunSimulationDialog
        isOpen={isRunDialogOpen}
        onClose={handleCloseDialog}
        onSuccess={handleRunSuccess}
        rerunBatch={rerunBatch}
      />
    </div>
  )
}

export default SimulationsPage
